import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import ReactPlayer from 'react-player';
import CustomVideoPlayerControlPanel from './playerPanel';
import { wait } from '@utils/common';
import ElementUtil from '@utils/elementUtil';
import classNames from 'classnames/bind';
import styles from './v4Video.module.scss';

const cx = classNames.bind(styles);

interface V4Video {
  url?: string;
}

const BIG_TIME = 60 * 60;
const VIDEO_WIDTH = 430;
const VIDEO_HEIGHT = 322;

const V4Video = ({ url }: V4Video) => {
  const player = useRef<ReactPlayer>(null);
  const effectRef = useRef<HTMLDivElement>(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [currentSeek, setCurrentSeek] = useState(0);
  const [totalDuration, setTotalDuration] = useState(0);
  const [displayPlayBtn, setDisplayPlayBtn] = useState(false);

  const handleOnProgress = (e: { played: number; playedSeconds: number; loaded: number; loadedSeconds: number }) => {
    if (e.playedSeconds === totalDuration) {
      setCurrentSeek(0);
      if (player.current) {
        player.current.seekTo(0);
      }
      setDisplayPlayBtn(true);
    } else {
      setCurrentSeek(e.playedSeconds);
    }
  };

  const handleChangeSeek = (e: any) => {
    setCurrentSeek(e.target.value);
    if (player.current) player.current.seekTo(e.target.value);
  };

  const handleClickPlay = () => {
    setIsPlaying(true);

    setTimeout(() => {
      if (player.current) player.current.seekTo(0);
    }, 50);
  };

  const handleClickPause = () => {
    setIsPlaying(false);
  };

  const handleClickVideo = () => {
    if (isPlaying) handleClickPause();
    else handleClickPlay();
  };

  const onReadyVideo = () => {
    if (player.current) {
      if (player.current.getDuration() === Infinity) {
        player.current.seekTo(BIG_TIME);
      } else {
        setTotalDuration(player.current.getDuration());
      }
    }
  };

  useEffect(() => {
    if (url) {
      setDisplayPlayBtn(isPlaying);
      (async () => {
        if (effectRef.current) {
          effectRef.current.style.opacity = '0.64';
          effectRef.current.style.transform = 'scale(1)';
        }
        await wait(500);
        ElementUtil.fadeOut(effectRef);
        if (effectRef.current) {
          effectRef.current.style.transform = 'scale(1.2)';
        }
      })();
    }
  }, [isPlaying]);

  useEffect(() => {
    setDisplayPlayBtn(true);
    if (effectRef.current) {
      effectRef.current.style.opacity = '1';
      effectRef.current.style.transform = 'scale(1)';
    }

    return () => {
      setCurrentSeek(0);
      setIsPlaying(false);
    };
  }, [url]);

  if (!url)
    return (
      <div className={cx('tempWrap')}>
        <p className={cx('text')}>영상을 확인할 수 없어요.</p>
      </div>
    );

  return (
    <div className={cx('player', { temp: !url })} style={{ width: VIDEO_WIDTH, height: VIDEO_HEIGHT }}>
      <div className={cx('effect')} ref={effectRef}>
        <svg className={cx('playPause')}>
          {displayPlayBtn ? (
            <polygon points={'8,2 32,16 8,30'} />
          ) : (
            <>
              <rect width="12" height="32" />
              <rect x="20" width="12" height="32" />
            </>
          )}
        </svg>
      </div>
      <button onClick={handleClickVideo}>
        <ReactPlayer
          ref={player}
          width={VIDEO_WIDTH}
          height={VIDEO_HEIGHT}
          playing={isPlaying}
          url={url}
          onReady={onReadyVideo}
          onProgress={(e) => handleOnProgress(e)}
          onEnded={handleClickPause}
        />
      </button>
      <CustomVideoPlayerControlPanel
        isPlaying={isPlaying}
        totalDuration={totalDuration}
        currentSeek={currentSeek}
        onClickPlay={handleClickPlay}
        onClickPause={handleClickPause}
        onChangeSeek={handleChangeSeek}
      />
    </div>
  );
};

export default V4Video;
